// src/integrations/formConfigAdapters/index.js

import bitlyFormConfigAdapter from './bitlyFormConfigAdapter';

const PROVIDER_ADAPTERS = {
  [bitlyFormConfigAdapter.provider]: bitlyFormConfigAdapter,
};

function asStringOrNull(value) {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  return trimmed || null;
}

export function getProviderId(provider) {
  return asStringOrNull(provider)?.toLowerCase() || null;
}

export function getProviderAdapter(provider) {
  const providerId = getProviderId(provider);

  if (!providerId) {
    return null;
  }

  return PROVIDER_ADAPTERS[providerId] || null;
}

export function getKnownProviderIds() {
  return Object.keys(PROVIDER_ADAPTERS);
}

/**
 * Returns only providers explicitly present in the staged form config payload.
 *
 * Enterprise policy:
 * - Registry membership means "this provider is supported by this legacy
 *   form-publish sync path."
 * - Staged config presence means "this provider has been intentionally changed
 *   for this form/view mode and is eligible for sync/publish."
 * - A supported provider must NEVER be saved or published just because it is
 *   registered in the adapter registry.
 *
 * Important:
 * - BigQuery is intentionally not registered here.
 * - BigQuery is managed by the dedicated Integrations BigQuery modal and the
 *   backend BigQuery lifecycle callables.
 * - This prevents the legacy form-publish adapter path from reintroducing
 *   stale BigQuery fields such as dryRun, tableId, viewEnabled, or viewName.
 */
export function getStagedProviderIds(stagedConfigs = {}) {
  if (
    !stagedConfigs ||
    typeof stagedConfigs !== 'object' ||
    Array.isArray(stagedConfigs)
  ) {
    return [];
  }

  return Object.keys(stagedConfigs)
    .map((provider) => getProviderId(provider))
    .filter(Boolean);
}

/**
 * Returns staged providers that are allowed to participate in the publish
 * lifecycle.
 *
 * Important:
 * - This intentionally does NOT include all known providers.
 * - A disabled provider config can still be publishable if it is staged,
 *   because publishing `{ enabled: false }` is how a user intentionally turns
 *   an integration off for a form.
 */
export function getPublishableProviderIds(stagedConfigs = {}) {
  return getStagedProviderIds(stagedConfigs).filter((provider) => {
    const adapter = getProviderAdapter(provider);
    return adapter?.publishable === true;
  });
}

export function normalizeProviderConfig(provider, rawConfig = {}) {
  const adapter = getProviderAdapter(provider);

  if (!adapter || typeof adapter.serialize !== 'function') {
    return null;
  }

  return adapter.serialize(rawConfig);
}
